import { type AuditEntryPublic, type AuditGroup, AUDIT_GROUPS } from '@accounts/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { type CSSProperties, useEffect, useMemo, useState } from 'react';

import { STR } from '../../i18n';
import { ApiError, exportAudit, getOperationalSettings, listAudit } from '../../lib/api';
import { formatDateTime, formatMetadataValue, humanizeKey, relativeTime } from '../../lib/format';
import { queryKeys } from '../../lib/queryKeys';
import { showToast } from '../../lib/toast';
import { Modal } from '../../ui/Modal';
import { Callout, Skeleton } from '../../ui/Primitives';
import {
  cardStyle,
  detailPaneStyle,
  eyebrowStyle,
  modalButtonStyle,
  monoStyle,
  quietButtonStyle,
  rowActionStyle,
  searchFieldStyle,
} from '../../ui/styles';

import { AdminShell } from './AdminShell';

/**
 * The audit log — a read-only history, and the one screen in this panel whose whole job is to
 * be believed. Three rules run through every line of it:
 *
 *   ONE DENOMINATOR. `meta.total` is the count of rows matching the CURRENT filters, and it
 *   is what the result count states. `data.length` is capped at `limit` and would under-report
 *   the moment the log outgrows a page — so nothing on this screen is counted from the array.
 *
 *   NOTHING IS INVENTED. An entry with no principal (the operator CLI, an anonymous failed
 *   sign-in) says so in words; an entry with no recorded address renders an em dash. Neither
 *   is ever filled in with a plausible-looking stand-in, because a guessed value in an audit
 *   log is worse than an absent one.
 *
 *   THE FILTERS ARE THE SERVER'S. `group` is a group, not a prefix — `apps` spans two verb
 *   namespaces and `operator` is an actor-type predicate — so it is resolved server-side and
 *   this screen never classifies a verb itself. It does not filter rows in the browser at all.
 */

/**
 * RULED (Wave 3B fix round): rows per page is the `auditPageSize` operational setting, read
 * live from the settings endpoint. This value stands in ONLY if that read itself fails — it
 * is the endpoint's own `limit` default, not a second home for the setting. The operator
 * CLI's `list-audit` default (50) and the API's own default/max (50/200) are deliberately
 * independent of the setting: it is a screen preference, nothing more.
 */
const FALLBACK_PAGE_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 300;

type GroupFilter = 'all' | AuditGroup;

const GROUP_OPTIONS: readonly GroupFilter[] = ['all', ...AUDIT_GROUPS];

/**
 * Chip tone, decided from the VERB — ONE derivation, ruled in the Wave 3B fix round, and the
 * only place in the web layer that turns a verb into a tone. The rule reads the verb's
 * ENDING, so a future `things.create` is green without anyone maintaining a list:
 *
 *   create / grant / reactivate / restore  → green   (something now exists or works again)
 *   revoke / deactivate / retire           → soft red (something was taken away)
 *   update / edit / rename / recolor       → blue    (something changed shape)
 *   the security verbs                     → amber   (named exactly — they are refusals, not
 *                                                     CRUD, and no suffix rule should ever
 *                                                     grow into them)
 *   anything else                          → neutral, unchanged
 *
 * `endsWith` rather than a `.suffix` match, deliberately: `settings.operational_update` and
 * `settings.branding_update` are updates and must read as updates — the underscore form is
 * how the settings namespace spells its verbs.
 *
 * Wave 3D adds four endings for the two managed vocabularies this round introduces —
 * `announcement_types.{create,rename,recolor,retire}` and
 * `application_tags.{create,rename,retire,restore}`. Without them, three quarters of both
 * verb sets would fall all the way to neutral, and a screen that colours `create` green
 * while leaving `retire` grey reads as a rule that stopped working rather than one that
 * chose not to speak. `restore` is green for the same reason `reactivate` is: the thing
 * works again.
 */
const TONE_SUFFIXES: readonly (readonly [readonly string[], string])[] = [
  [['create', 'grant', 'reactivate', 'restore'], 'c-emerald'],
  [['revoke', 'deactivate', 'retire'], 'c-red'],
  [['update', 'edit', 'rename', 'recolor'], 'c-blue'],
];

/*
 * The `security` audit group's refusal verbs, named exactly and kept in step with the
 * server's own declaration of that group (`SECURITY` in server/src/modules/audit/service.ts).
 * All four must be listed: `2fa_failed` and `2fa_rate_limited` end in nothing the suffix
 * table matches, so an omitted verb does not fall back to some other tone — it falls all the
 * way to neutral, and the Audit screen then renders two of the four refusals in one group
 * amber and the other two grey. Wave 3C added the 2FA pair to the server group; they are
 * named here for the same reason the login pair is.
 *
 * These four are transcribed by hand from the server's list because the verb set is not a
 * `@accounts/shared` export — the same hand-sync exposure already registered against the
 * audit GROUP set. If that is ever fixed, this list should consume the shared export.
 */
const SECURITY_VERBS: readonly string[] = [
  'auth.login_failed',
  'auth.login_rate_limited',
  'auth.2fa_failed',
  'auth.2fa_rate_limited',
];

function chipClassFor(action: string): string {
  if (SECURITY_VERBS.includes(action)) return 'c-amber';
  for (const [suffixes, cls] of TONE_SUFFIXES) {
    if (suffixes.some((suffix) => action.endsWith(suffix))) return cls;
  }
  return 'c-slate';
}

interface ActorLine {
  name: string;
  sub: string;
  muted: boolean;
}

/**
 * THE POINT OF THE SCREEN. Two kinds of entry legitimately have no user behind them, and both
 * must read as deliberate rather than as a row that failed to load.
 */
function actorLineFor(entry: AuditEntryPublic): ActorLine {
  if (entry.actorType === 'operator_cli') {
    return { name: STR.audit.actorOperatorCli, sub: STR.audit.actorOperatorCliSub, muted: true };
  }
  // `actor === null` is the anonymous case, and also the only honest reading of any row whose
  // principal did not resolve — it is never turned into a name.
  if (entry.actorType === 'anonymous' || entry.actor === null) {
    return { name: STR.audit.actorNone, sub: STR.audit.actorNoneSub, muted: true };
  }
  return { name: entry.actor.name, sub: entry.actor.email, muted: false };
}

/**
 * `ip` is `string | null` in the contract, and absent altogether on a server that has not yet
 * shipped the field. `?? null` collapses both to the same answer, so an absent address renders
 * exactly as a null one does — an em dash — and never as `undefined`.
 */
function addressOf(entry: AuditEntryPublic): string | null {
  return entry.ip ?? null;
}

/**
 * `datetime-local` yields `YYYY-MM-DDTHH:mm` with NO offset, which the API refuses outright
 * (400). The value is read as the operator's own wall clock — which is what they typed — and
 * converted to a real instant before it is sent.
 */
function localInputToIso(value: string): string | null {
  if (value === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

const CSV_COLUMNS = [
  'when',
  'actor_name',
  'actor_email',
  'action',
  'target_type',
  'target_id',
  'ip',
] as const;

/**
 * Quoting alone is correct CSV and still not safe to open. A cell whose first character is
 * `=`, `+`, `-` or `@` is executed as a FORMULA by Excel and Sheets, so a spreadsheet is a
 * code path, not a document — and two of these columns carry text this app deliberately
 * does not validate: `actor_name`, and `ip`, which D31 stores as unconstrained text
 * precisely so an odd address form cannot roll back the mutation it records. Neutralise the
 * leading character rather than rejecting the value: an audit export must render what was
 * recorded, including something that looks like an attack, without becoming one.
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

function csvCell(value: string | null): string {
  const raw = value ?? '';
  const safe = FORMULA_LEAD.test(raw) ? `'${raw}` : raw;
  return `"${safe.split('"').join('""')}"`;
}

function buildCsv(entries: readonly AuditEntryPublic[]): string {
  const lines = [CSV_COLUMNS.join(',')];
  for (const entry of entries) {
    const actor = actorLineFor(entry);
    lines.push(
      [
        entry.createdAt,
        // The honest label for an actorless row, the real name otherwise. Never a blank that
        // reads as data loss, never a name that was not there.
        entry.actor?.name ?? actor.name,
        entry.actor?.email ?? null,
        entry.action,
        entry.targetType,
        entry.targetId,
        addressOf(entry),
      ]
        .map(csvCell)
        .join(','),
    );
  }
  return lines.join('\n');
}

function downloadCsv(contents: string): void {
  const blob = new Blob([contents], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'accounts-audit-log.csv';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 4000);
}

const filterRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  padding: '10px 12px',
  borderBottom: '1px solid var(--border)',
};

const stampFieldStyle: CSSProperties = {
  padding: '6px 8px',
  borderRadius: 8,
  fontSize: 11.5,
};

const gridHeaderStyle: CSSProperties = {
  ...eyebrowStyle,
  padding: '10px 18px',
  background: 'var(--surface-2)',
  borderBottom: '1px solid var(--border)',
};

const gridRowStyle: CSSProperties = {
  padding: '11px 18px',
  borderBottom: '1px solid var(--border)',
};

const actionChipStyle: CSSProperties = {
  ...monoStyle,
  fontSize: 11,
  padding: '2px 7px',
  borderRadius: 6,
  borderWidth: 1,
  borderStyle: 'solid',
  whiteSpace: 'nowrap',
};

const truncatedSubStyle: CSSProperties = {
  display: 'block',
  fontSize: 11,
  marginTop: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export function AuditPage() {
  const [group, setGroup] = useState<GroupFilter>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<AuditEntryPublic | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  /*
   * CHANGING A FILTER RESETS TO PAGE ONE (ruled) — page 4 of one filter's rows is not a
   * place in another filter's. Every path that changes a filter runs through here: the
   * three direct handlers below, the search debounce (only when the debounced value
   * genuinely changed — retyped whitespace is not a new filter), and Clear filters. A
   * stale export refusal goes with it: its numbers described a set that no longer is.
   */

  // One request per pause, not one per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = search.trim();
      // `debouncedSearch` is a dependency so this comparison reads the live value; when it
      // is what just changed, the re-armed timer finds nothing to do and writes no state.
      if (next === debouncedSearch) return;
      setDebouncedSearch(next);
      setPage(0);
      setExportError(null);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [search, debouncedSearch]);

  const fromIso = localInputToIso(from);
  const toIso = localInputToIso(to);

  /*
   * THE PAGE SIZE IS THE SETTING (`auditPageSize`), read from the same operational-settings
   * endpoint the Settings screen saves to — never a constant of this file's own. `null`
   * means "not known yet": the list below is deliberately NOT asked for until the setting
   * arrives, so this screen never renders a page whose size is not the ruled one. If the
   * settings read itself fails, the endpoint's own default stands in — the log must stay
   * readable even when a preference cannot be.
   *
   * WHEN A CHANGE TAKES EFFECT — deliberate, not incidental: on this screen's next visit
   * (the settings read is refreshed on mount), and immediately if the value ever changes
   * while the screen is open (the render-phase guard below) — in which case the pager
   * resets to page one, because page 4 under one size is not a place under another. The
   * same rule the filters already follow, applied BEFORE the list query can run under the
   * new size, so a stale page number is never fetched.
   */
  const operationalQuery = useQuery({
    queryKey: queryKeys.operationalSettings,
    queryFn: getOperationalSettings,
  });
  const pageSize =
    operationalQuery.data?.settings.auditPageSize ??
    (operationalQuery.isError ? FALLBACK_PAGE_LIMIT : null);
  const effectivePageSize = pageSize ?? FALLBACK_PAGE_LIMIT;

  const [seenPageSize, setSeenPageSize] = useState(effectivePageSize);
  if (seenPageSize !== effectivePageSize) {
    setSeenPageSize(effectivePageSize);
    setPage(0);
  }

  const params = useMemo(
    () => ({
      ...(group === 'all' ? {} : { group }),
      ...(debouncedSearch === '' ? {} : { q: debouncedSearch }),
      ...(fromIso === null ? {} : { from: fromIso }),
      ...(toIso === null ? {} : { to: toIso }),
    }),
    [group, debouncedSearch, fromIso, toIso],
  );

  const auditQuery = useQuery({
    queryKey: queryKeys.audit({
      group,
      q: debouncedSearch,
      from: fromIso ?? '',
      to: toIso ?? '',
      page,
      limit: effectivePageSize,
    }),
    queryFn: () =>
      listAudit({ ...params, limit: effectivePageSize, offset: page * effectivePageSize }),
    enabled: pageSize !== null,
    // Changing a filter or page keeps the rows already on screen while the next set is
    // fetched — skeletons flashing over real content read as a fault that did not happen.
    placeholderData: keepPreviousData,
  });

  /*
   * First-paint pending covers BOTH reads: the setting the page size comes from, and the
   * first page of rows (`isPending` stays true while the list is gated on the setting, so
   * the skeleton — never the empty-log screen — is what renders in that interval).
   */
  const pending = auditQuery.isPending;

  const rows = auditQuery.data?.data ?? [];
  // THE count. Never `rows.length`, which is capped at the page size.
  const total = auditQuery.data?.meta.total ?? null;
  const pageCount =
    total === null ? null : Math.max(1, Math.ceil(total / effectivePageSize));

  // An unfiltered total of 0 is an empty LOG; a filtered total of 0 is a search that matched
  // nothing. They are different facts and get different screens.
  const filtersActive = group !== 'all' || debouncedSearch !== '' || from !== '' || to !== '';

  function clearFilters() {
    setGroup('all');
    setSearch('');
    setDebouncedSearch('');
    setFrom('');
    setTo('');
    setPage(0);
    setExportError(null);
  }

  /**
   * Exports what the FILTER SELECTS — the WHOLE filtered set, never the visible page, in
   * ONE call to the export endpoint. The cap is the server's: a set beyond it is refused
   * with `export_too_large`, whose message names both the matching count and the cap and is
   * rendered VERBATIM below the filters — never paraphrased, never reduced to its code.
   * There is deliberately no client-side paging loop left here to walk past the cap with.
   */
  async function exportCsv(): Promise<void> {
    if (exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const result = await exportAudit(params);

      if (result.data.length === 0) {
        showToast(STR.audit.nothingToExport, 'warn');
        return;
      }

      downloadCsv(buildCsv(result.data));
      showToast(STR.toast.auditExported(result.data.length));
    } catch (error) {
      // The server's own sentence, verbatim — it is written for administrators. A toast
      // dismisses itself in seconds; a refusal the administrator must act on stays put.
      setExportError(error instanceof ApiError ? error.message : STR.errors.generic);
    } finally {
      setExporting(false);
    }
  }

  return (
    <AdminShell
      surfaceId="audit"
      primary={{
        // RULED WORDING: the button says it exports ALL matching rows. Before a total is
        // known (and when it is zero) there is no honest number to print, so the plain
        // label stands in.
        label: exporting
          ? STR.audit.exporting
          : total === null || total === 0
            ? STR.audit.exportCsv
            : STR.audit.exportAll(total),
        onClick: () => {
          void exportCsv();
        },
      }}
    >
      <div style={detailPaneStyle}>
        <div style={{ ...cardStyle, width: '100%' }}>
          <div style={filterRowStyle}>
            <select
              className="field"
              value={group}
              onChange={(event) => {
                setGroup(event.target.value as GroupFilter);
                setPage(0);
                setExportError(null);
              }}
              aria-label={STR.audit.actionTypeLabel}
              style={{ padding: '7px 30px 7px 10px', borderRadius: 8, fontSize: 12.5 }}
            >
              {GROUP_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {STR.audit.groups[option]}
                </option>
              ))}
            </select>

            <input
              className="field"
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              placeholder={STR.audit.searchLabel}
              aria-label={STR.audit.searchLabel}
              style={{ ...searchFieldStyle, flex: 1, minWidth: 170, width: 'auto' }}
            />

            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 650 }}>
                {STR.audit.fromLabel}
              </span>
              <input
                className="field"
                type="datetime-local"
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value);
                  setPage(0);
                  setExportError(null);
                }}
                aria-label={STR.audit.fromFieldLabel}
                style={stampFieldStyle}
              />
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 650 }}>
                {STR.audit.toLabel}
              </span>
              <input
                className="field"
                type="datetime-local"
                value={to}
                onChange={(event) => {
                  setTo(event.target.value);
                  setPage(0);
                  setExportError(null);
                }}
                aria-label={STR.audit.toFieldLabel}
                style={stampFieldStyle}
              />
            </label>

            {/* Absent until a total is known — a count of zero while loading is a claim. */}
            {total === null ? null : (
              <span
                className="tx-subtle"
                style={{
                  marginLeft: 'auto',
                  fontSize: 12.5,
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              >
                {STR.audit.count(total)}
              </span>
            )}
          </div>

          {/*
           * The export refusal, VERBATIM — the server's message names the matching row
           * count and the cap, in words written for administrators. It stays until the
           * filters change or another export is attempted; a toast would dismiss a sentence
           * somebody needs in order to decide how far to narrow.
           */}
          {exportError === null ? null : (
            <div style={{ padding: '12px 12px 0' }}>
              <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
                {exportError}
              </Callout>
            </div>
          )}

          {pending ? <AuditSkeleton /> : null}

          {/*
           * A FAILED READ AND AN EMPTY LOG MUST NOT LOOK THE SAME. Showing "No events yet"
           * here would tell an administrator nothing has happened when the truth is that
           * nobody was able to ask.
           */}
          {!pending && auditQuery.isError ? (
            <div style={{ padding: '18px 16px' }}>
              <Callout
                tone="danger"
                role="alert"
                style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
              >
                <span style={{ flex: 1, minWidth: 180 }}>{STR.errors.couldNotLoad}</span>
                <button
                  type="button"
                  className="tbtn"
                  style={quietButtonStyle}
                  onClick={() => {
                    void auditQuery.refetch();
                  }}
                >
                  {STR.common.tryAgain}
                </button>
              </Callout>
            </div>
          ) : null}

          {!pending && !auditQuery.isError && rows.length === 0 && filtersActive ? (
            <div style={{ padding: '34px 16px', textAlign: 'center' }}>
              <div className="tx-subtle" style={{ fontSize: 13 }}>
                {STR.audit.noMatch}
              </div>
              <button
                type="button"
                className="tbtn"
                onClick={clearFilters}
                style={{ ...rowActionStyle, marginTop: 7 }}
              >
                {STR.audit.clearFilters}
              </button>
            </div>
          ) : null}

          {!pending && !auditQuery.isError && rows.length === 0 && !filtersActive ? (
            <div style={{ padding: '56px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 15.5, fontWeight: 650 }}>{STR.audit.emptyTitle}</div>
              <p
                className="tx-subtle"
                style={{
                  maxWidth: 420,
                  margin: '8px auto 0',
                  fontSize: 13,
                  lineHeight: 1.6,
                  textWrap: 'pretty',
                }}
              >
                {STR.audit.emptyBody}
              </p>
            </div>
          ) : null}

          {!pending && !auditQuery.isError && rows.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 860 }}>
                <div className="audit-grid tx-subtle" style={gridHeaderStyle}>
                  <span>{STR.audit.columns.when}</span>
                  <span>{STR.audit.columns.actor}</span>
                  <span>{STR.audit.columns.action}</span>
                  <span>{STR.audit.columns.target}</span>
                  <span>{STR.audit.columns.ip}</span>
                  <span aria-label={STR.audit.columns.view} />
                </div>

                {rows.map((entry) => {
                  const actor = actorLineFor(entry);
                  const address = addressOf(entry);
                  return (
                    <div key={entry.id} className="audit-grid row" style={gridRowStyle}>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600 }}>
                          {relativeTime(entry.createdAt)}
                        </span>
                        <span
                          className="tx-subtle"
                          style={{ display: 'block', fontSize: 11, marginTop: 1 }}
                        >
                          {formatDateTime(entry.createdAt)}
                        </span>
                      </div>

                      <div style={{ minWidth: 0 }}>
                        <span
                          className={actor.muted ? 'tx-subtle' : undefined}
                          style={{
                            display: 'block',
                            fontSize: 12.5,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {actor.name}
                        </span>
                        <span className="tx-subtle" style={truncatedSubStyle}>
                          {actor.sub}
                        </span>
                      </div>

                      <div style={{ minWidth: 0 }}>
                        <code className={chipClassFor(entry.action)} style={actionChipStyle}>
                          {entry.action}
                        </code>
                      </div>

                      <div style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600 }}>
                          {entry.targetType ?? STR.common.dash}
                        </span>
                        <span
                          className="tx-subtle"
                          style={{ ...truncatedSubStyle, ...monoStyle }}
                        >
                          {entry.targetId ?? STR.common.dash}
                        </span>
                      </div>

                      <div
                        className="tx-subtle"
                        style={{
                          ...monoStyle,
                          fontSize: 11.5,
                          whiteSpace: 'nowrap',
                          paddingTop: 2,
                        }}
                      >
                        {address ?? STR.common.dash}
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="tbtn"
                          aria-label={STR.audit.viewEntry(entry.action)}
                          onClick={() => {
                            setSelected(entry);
                          }}
                          style={rowActionStyle}
                        >
                          {STR.audit.columns.view}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/*
           * THE PAGER — real pagination, ruled at 50 rows per page. Renders IFF the
           * filtered set spans more than one page, so its absence is a statement too: what
           * is on screen is all of it. Both numbers derive from `meta.total`; the buttons
           * disable at the ends rather than vanishing, so the row never reflows underfoot.
           */}
          {pageCount === null || pageCount <= 1 ? null : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 12px',
                borderTop: '1px solid var(--border)',
              }}
            >
              <span
                className="tx-subtle"
                style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}
              >
                {STR.audit.pageOf(page + 1, pageCount)}
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  className="tbtn"
                  style={rowActionStyle}
                  disabled={page === 0}
                  onClick={() => {
                    setPage(page - 1);
                  }}
                >
                  {STR.audit.previousPage}
                </button>
                <button
                  type="button"
                  className="tbtn"
                  style={rowActionStyle}
                  disabled={page + 1 >= pageCount}
                  onClick={() => {
                    setPage(page + 1);
                  }}
                >
                  {STR.audit.nextPage}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selected === null ? null : (
        <AuditDetailModal
          entry={selected}
          onClose={() => {
            setSelected(null);
          }}
        />
      )}
    </AdminShell>
  );
}

/** The audit screen's own first paint — the shape of the rows that are coming, not a rail. */
function AuditSkeleton() {
  return (
    <div style={{ overflowX: 'auto' }} aria-hidden="true">
      <div style={{ minWidth: 860 }}>
        {[0, 1, 2, 3, 4, 5].map((row) => (
          <div key={row} className="audit-grid" style={gridRowStyle}>
            <div>
              <Skeleton style={{ height: 11, width: 62, borderRadius: 5 }} />
              <Skeleton style={{ height: 9, width: 88, borderRadius: 5, marginTop: 5 }} />
            </div>
            <div>
              <Skeleton style={{ height: 11, width: 104, borderRadius: 5 }} />
              <Skeleton style={{ height: 9, width: 140, borderRadius: 5, marginTop: 5 }} />
            </div>
            <div>
              <Skeleton style={{ height: 17, width: 128, borderRadius: 6 }} />
            </div>
            <div>
              <Skeleton style={{ height: 11, width: 60, borderRadius: 5 }} />
              <Skeleton style={{ height: 9, width: 116, borderRadius: 5, marginTop: 5 }} />
            </div>
            <div>
              <Skeleton style={{ height: 11, width: 78, borderRadius: 5 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Skeleton style={{ height: 22, width: 44, borderRadius: 7 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * One entry in full. The context table is the metadata the server recorded, read as written;
 * "Show raw" is the escape hatch beneath it, for when the parsed view is not enough.
 */
function AuditDetailModal({
  entry,
  onClose,
}: {
  entry: AuditEntryPublic;
  onClose: () => void;
}) {
  const [showRaw, setShowRaw] = useState(false);

  const actor = actorLineFor(entry);
  const address = addressOf(entry);
  const context = Object.entries(entry.metadata ?? {});

  /*
   * `ip` sits at the TOP LEVEL here, beside the entry's other first-class facts — not inside
   * `context`. Older rows carry an `ip` KEY in their metadata from before the address had a
   * column of its own; that one stays where it was written, in `context`, because rewriting
   * history to look tidier is exactly what an audit log must not do.
   */
  const raw = JSON.stringify(
    {
      id: entry.id,
      at: entry.createdAt,
      action: entry.action,
      actor:
        entry.actorId === null
          ? null
          : {
              id: entry.actorId,
              email: entry.actor?.email ?? null,
              name: entry.actor?.name ?? null,
            },
      target:
        entry.targetType === null ? null : { type: entry.targetType, id: entry.targetId },
      ip: address,
      context: entry.metadata ?? {},
    },
    null,
    2,
  );

  const footerText = STR.audit.detail.footer(
    address === null ? STR.audit.detail.ipUnknown : STR.audit.detail.ipKnown(address),
    entry.actorId === null
      ? STR.audit.detail.noActingUser
      : STR.audit.detail.actingUser(entry.actorId),
  );

  return (
    <Modal
      title={STR.audit.detail.title}
      width="min(760px,100%)"
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%' }}>
          <span
            className="tx-subtle"
            style={{
              ...monoStyle,
              fontSize: 11,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {footerText}
          </span>
          <button
            type="button"
            className="prim"
            onClick={onClose}
            style={{ ...modalButtonStyle, marginLeft: 'auto', flex: 'none' }}
          >
            {STR.common.close}
          </button>
        </div>
      }
    >
      <div
        style={{
          padding: '10px 22px 0',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <code
          className={chipClassFor(entry.action)}
          style={{
            ...monoStyle,
            fontSize: 11.5,
            padding: '3px 9px',
            borderRadius: 6,
            borderWidth: 1,
            borderStyle: 'solid',
          }}
        >
          {entry.action}
        </code>
        <span className="tx-subtle" style={{ fontSize: 12 }}>
          {STR.audit.detail.stamp(relativeTime(entry.createdAt), formatDateTime(entry.createdAt))}
        </span>
      </div>

      <div
        style={{ padding: '16px 22px 18px', display: 'flex', flexDirection: 'column', gap: 13 }}
      >
        <div
          style={{
            padding: '11px 13px',
            borderRadius: 9,
            border: '1px solid var(--border)',
            background: 'var(--surface-2)',
          }}
        >
          <div
            className="tx-subtle"
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
            }}
          >
            {STR.audit.detail.actorLabel}
          </div>
          <div
            className={actor.muted ? 'tx-subtle' : undefined}
            style={{ fontSize: 13, fontWeight: 600, marginTop: 3 }}
          >
            {actor.name}
          </div>
          <div className="tx-subtle" style={{ fontSize: 11.5 }}>
            {actor.sub}
          </div>
        </div>

        <div style={{ border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
          <div
            className="tx-subtle"
            style={{
              ...eyebrowStyle,
              padding: '9px 13px',
              background: 'var(--surface-2)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            {STR.audit.detail.contextLabel}
          </div>
          {context.length === 0 ? (
            <div className="tx-subtle" style={{ padding: '12px 13px', fontSize: 12.5 }}>
              {STR.audit.detail.noContext}
            </div>
          ) : (
            context.map(([key, value]) => (
              <div
                key={key}
                style={{
                  display: 'flex',
                  gap: 14,
                  padding: '8px 13px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: 12.5,
                }}
              >
                <span className="tx-subtle" style={{ flex: 'none', width: 150, fontWeight: 600 }}>
                  {humanizeKey(key)}
                </span>
                <span style={{ minWidth: 0, ...monoStyle, fontSize: 12, overflowWrap: 'anywhere' }}>
                  {formatMetadataValue(value)}
                </span>
              </div>
            ))
          )}
        </div>

        <div>
          <button
            type="button"
            className="tbtn"
            aria-expanded={showRaw}
            onClick={() => {
              setShowRaw(!showRaw);
            }}
            style={quietButtonStyle}
          >
            {showRaw ? STR.audit.detail.hideRaw : STR.audit.detail.showRaw}
          </button>
          {showRaw ? (
            <pre
              style={{
                margin: '9px 0 0',
                padding: '12px 14px',
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 9,
                ...monoStyle,
                fontSize: 11,
                lineHeight: 1.55,
                overflow: 'auto',
                maxHeight: 240,
              }}
            >
              {raw}
            </pre>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
